import { CaptchaChallenge } from '@deizi/shared';
import { type FormEvent, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router';
import { z } from 'zod';
import { Captcha } from '../components/Captcha';
import { Alert } from '../components/States';
import { Icon } from '../components/Icon';
import { PasswordInput } from '../components/PasswordInput';
import { ApiClientError } from '../lib/api';
import { useLogin, useSession } from '../lib/session';
import { AuthLayout } from './AuthLayout';

const emailOk = (v: string) => z.email().safeParse(v.trim()).success;

/** The widget the API asks for in `details.captcha`, if any. */
function challengeFrom(err: unknown): CaptchaChallenge | null {
  if (!(err instanceof ApiClientError) || typeof err.details !== 'object' || !err.details) {
    return null;
  }
  const parsed = CaptchaChallenge.safeParse((err.details as { captcha?: unknown }).captcha);
  return parsed.success ? parsed.data : null;
}

/** Only allow in-app redirects after login. */
function safeNext(next: string | null): string {
  return next?.startsWith('/') && !next.startsWith('//') ? next : '/';
}

/** design/Login.dc.html; the two-step code view follows the same card (no board). */
export function LoginPage() {
  const session = useSession();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const login = useLogin();
  const [view, setView] = useState<'login' | 'forgot'>('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [remember, setRemember] = useState(true);
  const [touched, setTouched] = useState(false);
  // Two-step verification: after the right password the API asks for the app's code. Email and
  // password stay in memory and go again with the code.
  const [step, setStep] = useState<'password' | 'code'>('password');
  const [otp, setOtp] = useState('');
  const [recovery, setRecovery] = useState(false);
  // Captcha, once the API asks for it; a new key gives a fresh challenge after each attempt.
  const [challenge, setChallenge] = useState<CaptchaChallenge | null>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [captchaKey, setCaptchaKey] = useState(0);
  const [captchaMissing, setCaptchaMissing] = useState(false);

  if (session.data) return <Navigate to={safeNext(params.get('next'))} replace />;

  const emailBad = touched && !emailOk(email);
  const pwEmpty = touched && password === '';
  const code = login.error instanceof ApiClientError ? login.error.code : null;
  const wrong = code === 'unauthorized';
  const limited = code === 'rate_limited';
  const robot = captchaMissing || code === 'captcha_required';
  const captchaRefused = code === 'captcha_invalid';
  const wrongCode = code === 'two_factor_invalid';
  const otherError =
    login.error &&
    !wrong &&
    !limited &&
    !robot &&
    !captchaRefused &&
    !wrongCode &&
    code !== 'two_factor_required';
  const otpBad = touched && step === 'code' && otp.trim().length < 6;

  const send = () => {
    if (challenge && !captchaToken) {
      setCaptchaMissing(true);
      return;
    }
    setCaptchaMissing(false);
    login.mutate(
      {
        email: email.trim(),
        password,
        remember,
        ...(captchaToken ? { captchaToken } : {}),
        ...(step === 'code' ? { otp: otp.trim() } : {}),
      },
      {
        onSuccess: () => void navigate(safeNext(params.get('next')), { replace: true }),
        onError: (err) => {
          if (err instanceof ApiClientError && err.code === 'two_factor_required') {
            setStep('code');
            setTouched(false);
          }
          if (err instanceof ApiClientError && err.code === 'two_factor_invalid') setOtp('');
          // A token is good for one try: show a fresh challenge (or the first one).
          const next = challengeFrom(err) ?? challenge;
          setChallenge(next);
          setCaptchaToken(null);
          if (next) setCaptchaKey((k) => k + 1);
        },
      },
    );
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (step === 'password' && (!emailOk(email) || password === '')) return;
    if (step === 'code' && otp.trim().length < 6) return;
    send();
  };

  const backToPassword = () => {
    setStep('password');
    setOtp('');
    setRecovery(false);
    setPassword('');
    setTouched(false);
    login.reset();
  };

  if (view === 'forgot') {
    return (
      <AuthLayout>
        <div className="card pop col" style={{ width: '100%', gap: 18, padding: 28 }}>
          <div className="col" style={{ gap: 4 }}>
            <h1 style={{ fontSize: 24 }}>Recuperar senha</h1>
            <p className="muted sm">Peça ao Admin um link para criar uma senha nova.</p>
          </div>
          <Alert>
            O Admin abre <b>Configurações · Usuários</b>, escolhe <b>Redefinir senha</b> e envia o
            link para você pelo WhatsApp. O link vale por 24 horas.
          </Alert>
          <button type="button" className="btn g block" onClick={() => setView('login')}>
            Voltar para entrar
          </button>
        </div>
      </AuthLayout>
    );
  }

  const alerts = (
    <>
      {wrong ? (
        <Alert tone="err">
          <b>E-mail ou senha incorretos.</b> Confira e tente de novo.
        </Alert>
      ) : null}
      {wrongCode ? (
        <Alert tone="err">
          <b>Código incorreto.</b>{' '}
          {recovery
            ? 'Confira o código de recuperação; cada um vale uma vez.'
            : 'Use o código que aparece agora no app e confira se a hora do celular está certa.'}
        </Alert>
      ) : null}
      {limited ? (
        <Alert tone="err">
          <b>Muitas tentativas.</b> Espere alguns minutos e tente de novo.
        </Alert>
      ) : null}
      {robot ? (
        <Alert tone="err">
          <b>Confirme que você não é um robô.</b> Marque a verificação abaixo e entre de novo.
        </Alert>
      ) : null}
      {captchaRefused ? (
        <Alert tone="err">
          <b>A verificação não foi aceita.</b> Faça de novo e entre.
        </Alert>
      ) : null}
      {otherError ? (
        <Alert tone="err">
          <b>Não foi possível entrar.</b> O servidor não respondeu; tente de novo.
        </Alert>
      ) : null}
    </>
  );

  const captcha = challenge ? (
    <Captcha
      key={captchaKey}
      challenge={challenge}
      onToken={(t) => {
        setCaptchaToken(t);
        if (t) setCaptchaMissing(false);
      }}
    />
  ) : null;

  if (step === 'code') {
    return (
      <AuthLayout>
        <form
          className="card pop col"
          style={{ width: '100%', gap: 18, padding: 28 }}
          aria-label="Verificação em duas etapas"
          onSubmit={submit}
          noValidate
        >
          <div className="col" style={{ gap: 4 }}>
            <h1 style={{ fontSize: 24 }}>Verificação em duas etapas</h1>
            <p className="muted sm">
              {recovery
                ? 'Digite um dos códigos de recuperação que você guardou ao ativar.'
                : 'Abra o app autenticador no celular e digite o código de 6 dígitos da Deizi3d.'}
            </p>
          </div>
          {alerts}
          <div className="field">
            <label htmlFor="otp">{recovery ? 'Código de recuperação' : 'Código do app'}</label>
            <input
              key={recovery ? 'rec' : 'otp'}
              id="otp"
              className={otpBad ? 'input err tab' : 'input tab'}
              autoComplete="one-time-code"
              inputMode={recovery ? 'text' : 'numeric'}
              autoCapitalize={recovery ? 'characters' : 'off'}
              placeholder={recovery ? 'XXXX-XXXX' : '000000'}
              maxLength={recovery ? 12 : 6}
              // oxlint-disable-next-line jsx-a11y/no-autofocus -- the only field on this step
              autoFocus
              value={otp}
              onChange={(e) =>
                setOtp(recovery ? e.target.value : e.target.value.replace(/\D/g, ''))
              }
              aria-invalid={otpBad || undefined}
              aria-describedby={otpBad ? 'otp-err' : undefined}
              style={{ fontSize: 20, letterSpacing: recovery ? 2 : 6, textAlign: 'center' }}
            />
            {otpBad ? (
              <span className="help err" id="otp-err">
                <Icon name="alert" size={14} />
                {recovery ? 'Digite o código de recuperação.' : 'Digite os 6 dígitos.'}
              </span>
            ) : null}
          </div>
          {captcha}
          <button type="submit" className="btn p block" disabled={login.isPending}>
            {login.isPending ? 'Entrando…' : 'Entrar'}
          </button>
          <div className="row between wrap" style={{ gap: 8 }}>
            <button
              type="button"
              className="btn sm g"
              onClick={() => {
                setRecovery((r) => !r);
                setOtp('');
                setTouched(false);
              }}
            >
              {recovery ? 'Usar o código do app' : 'Usar um código de recuperação'}
            </button>
            <button type="button" className="btn sm g" onClick={backToPassword}>
              Voltar
            </button>
          </div>
        </form>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <form
        className="card pop col"
        style={{ width: '100%', gap: 18, padding: 28 }}
        aria-label="Entrar"
        onSubmit={submit}
        noValidate
      >
        <div className="col" style={{ gap: 4 }}>
          <h1 style={{ fontSize: 24 }}>Entrar</h1>
          <p className="muted sm">Gestão da loja</p>
        </div>
        {alerts}
        <div className="field">
          <label htmlFor="em">E-mail</label>
          <input
            id="em"
            className={emailBad ? 'input err' : 'input'}
            type="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            aria-invalid={emailBad || undefined}
            aria-describedby={emailBad ? 'em-err' : undefined}
          />
          {emailBad ? (
            <span className="help err" id="em-err">
              <Icon name="alert" size={14} />
              Digite um e-mail válido, como nome@loja.com.br.
            </span>
          ) : null}
        </div>
        <div className="field">
          <div className="row between">
            <label htmlFor="pw">Senha</label>
            <button
              type="button"
              className="btn sm g"
              style={{ minHeight: 28, padding: 0 }}
              onClick={() => setView('forgot')}
            >
              Esqueci a senha
            </button>
          </div>
          <PasswordInput
            id="pw"
            value={password}
            onChange={setPassword}
            invalid={pwEmpty}
            autoComplete="current-password"
            describedBy={pwEmpty ? 'pw-err' : undefined}
          />
          {pwEmpty ? (
            <span className="help err" id="pw-err">
              <Icon name="alert" size={14} />
              Digite a senha.
            </span>
          ) : null}
        </div>
        <button
          type="button"
          className="check"
          role="checkbox"
          aria-checked={remember}
          onClick={() => setRemember((r) => !r)}
        >
          <span className={remember ? 'box on' : 'box'}>
            {remember ? <Icon name="check" size={14} strokeWidth={3} /> : null}
          </span>
          Manter conectado neste aparelho
        </button>
        {captcha}
        <button type="submit" className="btn p block" disabled={login.isPending}>
          {login.isPending ? 'Entrando…' : 'Entrar'}
        </button>
      </form>
    </AuthLayout>
  );
}
